import { ButtonProps, CTAButton, SecondaryCTAButton } from '@buoyhealth/common.buoy-button';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import React, { useContext, useEffect, useState } from 'react';
import CircularArrowIcon from 'src/components/icons/CircularArrow';
import useIsEditing from 'src/interviews/hooks/useIsEditing';
import useIsPolaris from 'src/interviews/hooks/useIsPolaris';
import useTermsAgreed from 'src/interviews/hooks/useTermsAgreed';
import { LayoutContext } from '../Layout';
import TermsAndConditions from '../TermsAndConditions';
import Alert from '../Alert';
import { ContextType, getContext, getSubmitButtonVariant } from './utils';

import type { FC } from 'react';
import type { ISubmitButtonProps } from './types';

const btnStyles = {
  '&:not(.Mui-focusVisible)': {
    boxShadow: 'none',
  },
  height: '3.25rem',
  minWidth: '88px',
  paddingX: '1rem !important',
  lineHeight: 1,
  svg: {
    height: '16px',
    width: '16px',
  },
  '.MuiButton-endIcon': {
    marginLeft: '5px',
  },
  '.MuiButton-startIcon': {
    marginRight: 0,
  },
};

interface ButtonVariantProps extends Omit<ButtonProps, 'key' | 'contentEditable'> {
  buttonVariant?: 'default' | 'back' | 'next' | 'restart';
}

export const Button: FC<ButtonVariantProps> = ({
  children,
  buttonVariant = 'default',
  ...rest
}) => {
  const btnProps = {
    sx: btnStyles,
    ...rest,
  };

  switch (buttonVariant) {
    case 'back':
      return (
        <SecondaryCTAButton
          {...btnProps}
          className="bp-border-primary-main disabled:bp-border-gray-50"
          startIcon={<ArrowBackIos fontSize="small" />}
          size="small"
        >
          {children}
        </SecondaryCTAButton>
      );
    case 'next':
      return (
        <CTAButton
          {...btnProps}
          type="submit"
          endIcon={<ArrowForwardIos fontSize="small" />}
          size="small"
        >
          {children}
        </CTAButton>
      );
    case 'restart':
      return (
        <CTAButton {...btnProps} type="submit">
          <span className="bp-flex bp-h-full bp-items-center">
            {children}
            <span className="bp-ml-2">
              <CircularArrowIcon />
            </span>
          </span>
        </CTAButton>
      );
    default:
      return (
        <CTAButton {...btnProps} type="submit">
          <span className="bp-h-full">{children}</span>
        </CTAButton>
      );
  }
};

const SubmitButton = ({
  errorMessage = '',
  hideTopDivider = false,
  isRestart = false,
  isError = false,
  isIntermediary = false,
  onClick,
  onGoBackClick: propOnGoBackClick,
  text = 'Next',
  ...rest
}: ISubmitButtonProps) => {
  const isPolaris = useIsPolaris();
  const termsAgreed = useTermsAgreed();
  const [isTermsError, setIsTermsError] = useState(false);
  const isEditingAnswers = useIsEditing();

  useEffect(() => {
    if (termsAgreed && isTermsError) {
      setIsTermsError(false);
    }
  }, [termsAgreed, isTermsError]);

  const { onGoBackClick: contextOnGoBackClick } = useContext(LayoutContext) || {};
  const onGoBackClick = propOnGoBackClick || contextOnGoBackClick;

  const context = getContext(isIntermediary ? ContextType.Intermediary : ContextType.Question);
  const {
    question,
    shouldShowBackButton: contextShouldShowBackButton,
    shouldShowSubmitButton,
    totalQuestions,
  } = useContext(context) || {};

  const shouldShowBackButton = !!propOnGoBackClick || contextShouldShowBackButton;

  const handleClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!termsAgreed) {
      setIsTermsError(true);
    } else {
      onClick();
    }
  };

  if (!shouldShowSubmitButton && !shouldShowBackButton && !isRestart) {
    return null;
  }

  const showErrorMessage = isError || isTermsError;

  const containerClassName = isPolaris
    ? 'bp-right-0 bp-left-0 bp-bottom-0 bp-absolute bp-p-4 bp-bg-white bp-shadow-sm-up bp-z-10 bp-flex bp-flex-col bp-gap-4'
    : 'bp-w-full bp-mt-4 bp-flex bp-flex-col bp-gap-4';

  return (
    <>
      {isPolaris && !hideTopDivider && <div className="bp-h-[200px]" />}
      <div className={containerClassName}>
        <Alert
          message={
            isTermsError
              ? 'Oops! You must accept terms and conditions before continuing.'
              : errorMessage
          }
          mountOnEnter
          show={showErrorMessage}
        />
        {isEditingAnswers && (
          <Alert
            severity="info"
            message="Heads up! Editing your answers may change which questions are asked next."
            show={isEditingAnswers}
          />
        )}
        <TermsAndConditions />
        <div className="bp-row bp-grid bp-w-full bp-grid-cols-3 bp-bg-white">
          <div className="bp-flex bp-items-center bp-justify-start">
            {onGoBackClick && !!question && question?.index > 0 && shouldShowBackButton && (
              <Button onClick={onGoBackClick} buttonVariant="back" {...rest}>
                Back
              </Button>
            )}
          </div>

          <div className="bp-flex bp-items-center bp-justify-center">
            {!!question && totalQuestions && isPolaris && (
              <span className="bp-font-primary-small bp-text-gray-60">{`Question ${question.index + 1} of ${totalQuestions}`}</span>
            )}
          </div>

          <div className="bp-flex bp-items-center bp-justify-end">
            {(shouldShowSubmitButton || isRestart) && (
              <Button onClick={handleClick} buttonVariant={getSubmitButtonVariant(text)} {...rest}>
                {text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitButton;
